import { PlayerRef } from '@remotion/player';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { isEmpty } from 'radash';
import { Transition } from '@headlessui/react';
import { ResponsiveContainer } from '../../../../components/atoms/ResponsiveContainer';
import { getClipPlayerElementId } from '../../../../Pages/Clip/ClipPlayer/ClipPlayerUtils';
import { RemotionPlayerContext } from './context/RemotionPlayerContext';
import { useUpdateCurrentSrtIndex } from './hooks/useUpdateCurrentSrtIndex';
import { playerPlaybackRateStore } from '../stores/playerPlaybackRateStore';
import ClipPlayerControlsV2 from '../ClipPlayerControlsV2/ClipPlayerControlsV2';
import LowResolutionPreviewTag from '../LowResolutionPreviewTag/LowResolutionPreviewTag';
import RemotionPlayerOnly from './RemotionPlayerOnly';
import { useRemotionTimeSync } from './hooks/useRemotionTimeSync';
import { useTrackPlayerEvents } from './hooks/useTrackPlayerEvents';
import useRemotionPauseOnPlayerMenu from './hooks/useRemotionPauseOnPlayerMenu';
import { ElementType } from '@/player-config/src/types/Element/Type';
import {
  ElementPositions,
  SelectionElementType
} from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Element } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import PlayerMenus from '@/App/remotion/components/PlayerMenus/PlayerMenus';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import ClipHeader from '@/components/molecules/ClipHeader';
import ClipCustomizerMenu from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerMenu';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import FullTranscript from '@/components/molecules/Transcript/FullTranscript';
import {
  changeCaptionsPosition,
  isLayoutsWithVisionDataEnabled,
  isSplitsFeatureEnabled,
  isSplitsInTimelineEnabled
} from '@/stores/clip';
import {
  getAllCompositeSegments,
  isClipAudiogram,
  isFullLengthClip,
  shouldShowLowResolutionPreview
} from '@/player-config/src/utils/clipUtils';
import { ClipNewTimeline } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline';
import {
  TIMELINE_DISPLAY_TYPE,
  TRANSCRIPT_DISPLAY_TYPE
} from '@/Pages/Clip/ClipPlayer/ClipTimelineV2/ClipTimelineTypes';
import {
  getPlayerTimeFromAbsoluteTime,
  getPlayerTimeFromSegmentTime,
  getPreviousSegmentAbsoluteTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { formatTime } from '@/libs/core';
import { changeElementPositionHandler } from '@/Pages/Clip/ClipPlayer/ClipAssets/handlers';
import { setCurrentTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import usePlayerAspectRatio from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/hooks/usePlayerAspectRatio';
import AnalysisOverlay from '@/Pages/Clip/ClipPlayer/AnalysisOverlay';
import FailedOverlay from '@/Pages/Clip/ClipPlayer/FailedOverlay';
import UnavailableOverlay from '@/Pages/Clip/ClipPlayer/UnavailableOverlay';
import { AgenticChatLoader } from '@/components/organisms/AgenticChatLoader/AgenticChatLoader';
import { aiEditsStore } from '@/stores/aiEditsStore';
import { core } from '@/stores/core';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';

export default function ClipPlayerV2WithNewTimeline({
  playerClasses = '',
  canEditCaptions = false,
  showClipHeader = false,
  timelineDisplayType = TIMELINE_DISPLAY_TYPE.PROGRESS_BAR,
  transcriptDisplayType,
  isSharedPage = false,
  canEditSpeakerLabel = false,
  canEditImages = false,
  canEditTextElements = false,
  canEditVideos = false,
  canEditLottie = false
}: {
  playerClasses?: string;
  canEditCaptions?: boolean;
  showClipHeader?: boolean;
  timelineDisplayType?: TIMELINE_DISPLAY_TYPE;
  transcriptDisplayType?: TRANSCRIPT_DISPLAY_TYPE;
  isSharedPage?: boolean;
  canEditSpeakerLabel?: boolean;
  canEditImages?: boolean;
  canEditTextElements?: boolean;
  canEditVideos?: boolean;
  canEditLottie?: boolean;
}) {
  const remotionPlayerRef = useRef<PlayerRef>(null);
  const { clipId, clipData, fps, layoutStatus } = useClipsContext();
  const { onCreateClip } = useCreateClip({ clip: clipData });
  const playerConfig = clipData.asset_metadata.player_config;
  const playbackRate = useSyncExternalStore(playerPlaybackRateStore.subscribe, playerPlaybackRateStore.getSnapshot);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const aiEditsState = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);

  useUpdateCurrentSrtIndex({
    playerRef: remotionPlayerRef
  });

  useTrackPlayerEvents({
    playerRef: remotionPlayerRef,
    fps,
    clipId
  });

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const showPlayerFrames = featureFlags[FeatureFlagKeys.Use_CL_Show_Player_Frames] === true;
  const splitsInTimelineEnabled = isSplitsFeatureEnabled(featureFlags, clipData);
  const { compositionHeight, compositionWidth } = usePlayerAspectRatio({ size: clipData.asset_metadata.size });

  const onOutlineRelease = useCallback(
    (positions: ElementPositions, element?: Element) => {
      if (
        element?.type === ElementType.IMAGE ||
        element?.type === ElementType.VIDEO ||
        element?.type === ElementType.TEXT
      ) {
        changeElementPositionHandler(clipData, clipId, element.id, {
          containerStyle: positions,
          style: element.style || {}
        });
        return;
      }

      changeCaptionsPosition({
        clipId,
        caption_positions: {
          x: positions.left as any,
          y: positions.top as any,
          width: positions.width as any,
          height: positions.height as any
        },
        skipUpdate: false
      });
    },
    [clipId, clipData]
  );

  const onOutlineResize = useCallback(
    (positions: ElementPositions, selectionElementType: SelectionElementType) => {
      if (selectionElementType === SelectionElementType.CAPTIONS) {
        changeCaptionsPosition({
          clipId,
          caption_positions: {
            x: positions.left as any,
            y: positions.top as any,
            width: positions.width as any,
            height: positions.height as any
          },
          skipUpdate: true
        });
      }
    },
    [clipId]
  );
  /**
   * Includes the trim offset logic for the composite segment.
   */
  const playMainSectionFromTime = useCallback(
    (time: number, shouldPlay?: boolean) => {
      const remotionPlayer = remotionPlayerRef.current;
      const isPlaying = remotionPlayer?.isPlaying();
      if (!remotionPlayer) {
        return;
      }
      // !!TODO @HiteshChoudhary0098: Handle splits logic here
      const compositeSegments = getAllCompositeSegments(clipData) || [];

      if (compositeSegments.length === 0) {
        return;
      }
      const compositeSegment = compositeSegments[0];

      const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(compositeSegment, clipData, -Infinity);
      const seekTime = getPlayerTimeFromAbsoluteTime(time + previousSegmentAbsoluteTime, clipData, -Infinity);

      setCurrentTime(seekTime, fps, remotionPlayer);
      if (shouldPlay && !isPlaying) {
        remotionPlayer.play();
      } else {
        remotionPlayer.pause();
      }
    },
    [clipData, fps]
  );

  // Move this into FullTranscript after removal of Use_CL_New_Recording_Studio_Timeline
  const pausePlayer = useCallback(() => {
    remotionPlayerRef.current?.pause();
  }, []);

  const handleAIEditsSidebarClose = useCallback(() => {
    window.analytics?.track('ContentLabEvent', {
      name: 'AIEditsSidebarClosed',
      trigger: aiEditsState.sidebarOpenTrigger || 'Unknown',
      hadInteraction: aiEditsState.hadInteraction,
      clipId: clipId,
      mediaType: coreStore.content?.media_type
    });

    aiEditsStore.setIsAIEditsSidebarOpen(false);
  }, [aiEditsState, clipId, coreStore.content]);

  useEffect(() => {
    const visibilityChangeHandler = () => {
      if (document.visibilityState !== 'visible') {
        remotionPlayerRef.current?.pause();
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
  }, []);

  const getRenderTime = useCallback(
    (segmentTime: number, segmentId: string) => {
      return formatTime(getPlayerTimeFromSegmentTime(segmentTime, clipData, segmentId));
    },
    [clipData]
  );

  const { disableCaptionsDragging, disableCaptionsResize } = useMemo(() => {
    const isAudiogramClip = isClipAudiogram(clipData);

    return {
      disableCaptionsDragging: isAudiogramClip,
      disableCaptionsResize: isAudiogramClip
    };
  }, [clipData]);

  useRemotionTimeSync({ playerRef: remotionPlayerRef.current });

  const isFullClip = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  useRemotionPauseOnPlayerMenu({ remotionPlayerRef });

  const [showSpeakerLayoutProcessingOverlay, showSpeakerLayoutFailedOverlay, showSpeakerLayoutUnavailableOverlay] =
    useMemo(
      () => [layoutStatus === 'PROCESSING', layoutStatus === 'FAILED', layoutStatus === 'UNAVAILABLE'],
      [layoutStatus]
    );

  const { isAIEditsSidebarOpen } = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabled(clipData);
  }, [clipData]);

  useEffect(() => {
    if (isSplitsEnabled && isAIEditsSidebarOpen && !splitsInTimelineEnabled) {
      handleAIEditsSidebarClose();
    }
  }, [isSplitsEnabled, isAIEditsSidebarOpen, splitsInTimelineEnabled, handleAIEditsSidebarClose]);

  return (
    <RemotionPlayerContext.Provider value={{ playerRef: remotionPlayerRef.current }}>
      <div
        className="flex size-full"
        style={{
          maxWidth: isAIEditsSidebarOpen ? 'calc(100vw - 84px - clamp(12rem, 30vw, 26rem))' : 'calc(100vw - 84px)',
          width: isAIEditsSidebarOpen ? 'calc(100vw - 84px - clamp(12rem, 30vw, 26rem))' : 'calc(100vw - 84px)',
          paddingRight: isAIEditsSidebarOpen ? '1rem' : '0',
          transition: 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), padding-right 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'max-width, padding-right',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          '--clip-customizer-menu-width': isAIEditsSidebarOpen ? '1px' : '96px'
        }}
      >
        <div
          id={getClipPlayerElementId(clipId)}
          className={
            'flex max-w-[calc(100%-var(--clip-customizer-menu-width))] flex-1 flex-col items-center justify-center'
          }
        >
          <div className="flex size-full flex-row items-center justify-between">
            {transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? (
              <div
                className={classnames(
                  'relative h-full',
                  isAIEditsSidebarOpen
                    ? 'max-xl:w-0 max-xl:min-w-0 max-xl:overflow-hidden xl:w-[30%] xl:min-w-[calc(24rem)] xl:max-w-[calc(34rem)]'
                    : 'w-[30%] min-w-[calc(24rem)] max-w-[calc(34rem)]'
                )}
              >
                <FullTranscript
                  videoAssetId={clipId}
                  title={isFullClip ? 'Recording' : 'Clip'}
                  onCreateClip={onCreateClip}
                  onPause={pausePlayer}
                  fullClipOffset={0}
                  disableRedirectAfterCreateClip
                  getRenderTime={getRenderTime}
                  hideEndTimeStamp
                />
              </div>
            ) : (
              <></>
            )}
            <div
              className={classnames(
                'flex h-full flex-col',
                transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? 'flex-1' : 'w-full'
              )}
            >
              {showClipHeader && <ClipHeader newPlayer={true} />}
              <div className={classnames('flex flex-1 items-center justify-center rounded-md p-4', playerClasses)}>
                <ResponsiveContainer
                  id="player-main-section"
                  compositionHeight={compositionHeight}
                  compositionWidth={compositionWidth}
                  parentHeight={'90%'}
                  parentWidth={'90%'}
                  childClassnames="m-auto"
                >
                  {showSpeakerLayoutProcessingOverlay ? (
                    <AnalysisOverlay />
                  ) : showSpeakerLayoutFailedOverlay ? (
                    <FailedOverlay />
                  ) : showSpeakerLayoutUnavailableOverlay ? (
                    <UnavailableOverlay />
                  ) : (
                    <>
                      <div className="absolute inset-x-0 top-[-54px] z-[60]">
                        <PlayerMenus />
                      </div>
                      {/* ! TODO: @AshwinBhatkal to handle the state updates better */}
                      {playerConfig && !isEmpty(playerConfig) && (
                        <RemotionPlayerOnly
                          clipId={clipId}
                          remotionPlayerRef={remotionPlayerRef}
                          playerConfig={playerConfig}
                          durationInFrames={playerConfig.durationInFrames || 0}
                          compositionHeight={compositionHeight}
                          compositionWidth={compositionWidth}
                          playbackRate={playbackRate}
                          showPlayerFrames={showPlayerFrames}
                          canEditCaptions={canEditCaptions}
                          canEditImages={canEditImages}
                          canEditTextElements={canEditTextElements}
                          canEditVideos={canEditVideos}
                          canEditLottie={canEditLottie}
                          canManuallyReposition={true}
                          onOutlineRelease={onOutlineRelease}
                          onOutlineResize={onOutlineResize}
                          isSharedPage={isSharedPage}
                          canEditSpeakerLabel={canEditSpeakerLabel}
                          disableCaptionsDragging={disableCaptionsDragging}
                          disableCaptionsResize={disableCaptionsResize}
                          fps={fps}
                          preventOverlappingWithOtherCrops={isLayoutsWithVisionDataEnabled(clipData)}
                        />
                      )}
                      <LowResolutionPreviewTag isVisible={shouldShowLowResolutionPreview(clipData)} />
                    </>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          {timelineDisplayType === TIMELINE_DISPLAY_TYPE.PROGRESS_BAR ? <ClipPlayerControlsV2 /> : <ClipNewTimeline />}
        </div>
        <div className={isAIEditsSidebarOpen ? 'pointer-events-none invisible' : 'pointer-events-auto visible'}>
          <ClipCustomizerMenu
            isFullRecordingEdit={isFullClip}
            onTimeUpdate={playMainSectionFromTime}
            useNewRecordingsPlayer
          />
        </div>
      </div>
      <Transition
        show={isAIEditsSidebarOpen}
        enter="transition-all duration-300"
        enterFrom="translate-x-full opacity-0"
        enterTo="translate-x-0 opacity-100"
        leave="transition-all duration-300"
        leaveFrom="translate-x-0 opacity-100"
        leaveTo="translate-x-full opacity-0"
        className="fixed inset-y-0 right-0 z-[65] p-2"
        style={{
          width: 'clamp(12rem, 30vw, 26rem)',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <AgenticChatLoader onClose={handleAIEditsSidebarClose} />
      </Transition>
    </RemotionPlayerContext.Provider>
  );
}
